import { Switch } from "@msuite/ui/form";
import { icon } from "@msuite/ui/icons";
import { localized } from "@msuite/ui/localization";
import type { MenuItems } from "@msuite/ui/menu";
import { useDataTableContext } from "@msuite/ui/table";
import React from "react";
import RenderHooks from "render-hooks";

type FilterType = "closed" | "open";

function createFilterSwitch(type: FilterType, label: string): () => React.ReactNode {
	return () => (
		<RenderHooks hooks={hooks}>
			{({ useDataTableContext }) => {
				const context = useDataTableContext();
				const column = context.table.getColumn("isClosed");
				const filterValue = column?.getFilterValue() as boolean | undefined;

				const isClosedType = type === "closed";
				// Switch is checked when:
				// - For closed: filter === true (only closed) or undefined (both)
				// - For open: filter === false (only open) or undefined (both)
				const isChecked = isClosedType
					? filterValue === true || filterValue === undefined
					: filterValue === false || filterValue === undefined;

				const otherTypeChecked = isClosedType
					? filterValue === false || filterValue === undefined
					: filterValue === true || filterValue === undefined;

				function handleChange(nextChecked: boolean) {
					// If turning OFF this switch, turn ON the other one
					if (isChecked && !nextChecked) {
						// When this switch was ON and is now turned OFF,
						// we want the *other* filter to be active.
						// - If we were on "closed only" (true) and toggle it off → "open only" (false)
						// - If we were on "open only" (false) and toggle it off → "closed only" (true)
						// - If both were ON (undefined) and we toggle one off → the other stays ON
						column?.setFilterValue(!isClosedType);
						return;
					}

					// If turning ON this switch
					if (!isChecked && nextChecked) {
						if (otherTypeChecked) {
							// Both switches ON -> show all
							column?.setFilterValue(undefined);
						} else {
							// Only this switch ON
							column?.setFilterValue(isClosedType);
						}
					}
				}

				return (
					<div className="h-stack justify-between w-full">
						<p>{label}</p>
						<Switch
							checked={isChecked}
							onCheckedChange={handleChange}
						/>
					</div>
				);
			}}
		</RenderHooks>
	);
}

export function isClosedMenuOptions() {
	const menuOptions: MenuItems = [
		"divider",
		{
			label: "Open",
			icon: icon("RefreshCcw"),
			onClick: () => {},
			render: createFilterSwitch("closed", "Show Closed Statements"),
		},
		{
			label: "Close",
			icon: icon("Check"),
			onClick: () => {},
			render: createFilterSwitch("open", localized("measurements.show_open_statements")),
		},
	];

	return menuOptions;
}

const hooks = { useDataTableContext };
